import React, { useState, useRef, useEffect } from 'react';
import PickyKittyBubble from '../components/PickyKittyBubble';
import { Box, Button, Typography, Paper, CircularProgress, IconButton, Alert, Tooltip } from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import CardView from '../components/CardView';
import BrandsTooltip from '../components/BrandsTooltip';
import { buildApiUrl } from '../config/api';
import { apiCallFormData } from '../services/apiService';
import '../style/Scanner.css';

const Scanner = () => {
  const [image, setImage] = useState(null);
  const [matchingProducts, setMatchingProducts] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [stream, setStream] = useState(null);
  const [isBackCamera, setIsBackCamera] = useState(true);
  const videoRef = useRef(null);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [isCaptured, setIsCaptured] = useState(false);
  const [noProductsFound, setNoProductsFound] = useState(false);
  const [processingComplete, setProcessingComplete] = useState(false);
  const [extractedInfo, setExtractedInfo] = useState(null);
  const [brandsTooltipOpen, setBrandsTooltipOpen] = useState(false);

  // Cleanup function to stop camera when component unmounts
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  const startCamera = async (useBackCamera = true) => {
    try {
      setError(null);
      // First check if we have permission
      const permissions = await navigator.permissions.query({ name: 'camera' });
      if (permissions.state === 'denied') {
        throw new Error('Camera permission denied. Please enable camera access in your browser settings.');
      }

      // Stop any existing stream
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }

      const constraints = {
        video: {
          facingMode: { exact: useBackCamera ? "environment" : "user" },
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      };

      console.log('Requesting camera with constraints:', constraints);
      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      console.log('Camera stream obtained:', mediaStream);

      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        // Wait for video to be ready
        await new Promise((resolve) => {
          videoRef.current.onloadedmetadata = () => {
            resolve();
          };
        });
      }
    } catch (err) {
      console.error('Error accessing camera:', err);

      // Try fallback to any available camera
      try {
        console.log('Trying fallback camera...');
        const fallbackStream = await navigator.mediaDevices.getUserMedia({ video: true });
        setStream(fallbackStream);
        if (videoRef.current) {
          videoRef.current.srcObject = fallbackStream;
        }
        // Don't set error if fallback succeeds
        return;
      } catch (fallbackErr) {
        console.error('Fallback camera also failed:', fallbackErr);
        setError('Could not access any camera. Please check your device settings.');
      }
    }
  };



  const toggleCamera = async () => {
    if (stream) {
      stopCamera();
      setIsBackCamera(!isBackCamera);
      await startCamera(!isBackCamera);
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  };

  const captureImage = async () => {
    if (videoRef.current) {
      const canvas = document.createElement('canvas');
      const video = videoRef.current;

      // Set canvas dimensions to match video dimensions
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const ctx = canvas.getContext('2d');

      // Draw the video frame to the canvas
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Get the image data with high quality
      const imageData = canvas.toDataURL('image/jpeg', 1.0);
      setImage(imageData);
      stopCamera();
      setIsCaptured(true);
    }
  };

  const processImage = async () => {
    if (!image) {
      setError('Please capture an image first');
      return;
    }

    setIsProcessing(true);
    setError('');
    setMatchingProducts([]);
    setNoProductsFound(false);
    setProcessingComplete(false);

    try {
      // Convert base64 to blob
      const imageResponse = await fetch(image);
      const blob = await imageResponse.blob();

      const formData = new FormData();
      formData.append('image', blob, 'image.jpg');

      // Use the specialized FormData API service
      const ocrResponse = await apiCallFormData('api/ocr', formData);

      // Handle 429 status code specifically for daily limit
      if (ocrResponse.status === 429) {
        try {
          const data = await ocrResponse.json();
          setError(data.error || 'Daily scan limit reached. Please try again tomorrow.');
        } catch (parseError) {
          setError('Daily scan limit reached. Please try again tomorrow.');
        }
        return;
      }

      if (!ocrResponse.ok) {
        throw new Error(`HTTP error! status: ${ocrResponse.status}`);
      }

      const data = await ocrResponse.json();

      if (data.success) {
        // Store extracted information
        setExtractedInfo({
          brand: data.extracted_brand || data.brand,
          line: data.extracted_line || data.line,
          name: data.extracted_product_name || data.product_name,
          category: data.extracted_category || data.category,
          texture: data.extracted_texture || data.texture
        });

        // Check if at least one product has score >= 42
        const hasHighConfidenceMatch = (data.matching_products || []).some(product => product.match_score >= 42);

        let filteredProducts = [];

        if (hasHighConfidenceMatch) {
          // If at least one high-confidence match exists, show top 3 products regardless of score
          filteredProducts = (data.matching_products || [])
            .slice(0, 3)
            .map((product, index) => ({
              ...product,
              rank: index + 1
            }));
        } else {
          // If no high-confidence matches, show no products (current behavior)
          filteredProducts = [];
        }

        console.log('API Response:', data);
        console.log('All matching products:', data.matching_products);
        console.log('Has high confidence match (>=42):', hasHighConfidenceMatch);
        console.log('Final filtered products:', filteredProducts);

        setMatchingProducts(filteredProducts);
        setNoProductsFound(filteredProducts.length === 0);

        // If no products found but we have extracted info, show a helpful message
        if (filteredProducts.length === 0 && (data.brand || data.product_name)) {
          console.log('No high-confidence matches found, but product was detected');
        }
      } else {
        // Enhanced error handling
        let errorMessage = data.error || 'Failed to identify product';

        // Add specific error context
        if (data.error_type) {
          switch (data.error_type) {
            case 'daily_limit_reached':
              errorMessage = `Daily scan limit of ${data.limit || 100} reached. Please try again tomorrow.`;
              break;
            case 'empty_response':
              errorMessage = 'AI service returned an empty response. Please try again.';
              break;
            case 'json_parse_error':
              errorMessage = 'Invalid response format from AI service. Please try again.';
              break;
            case 'openai_auth_error':
            case 'anthropic_auth_error':
              errorMessage = 'API authentication failed. Please contact support.';
              break;
            case 'openai_rate_limit':
            case 'anthropic_rate_limit':
              errorMessage = 'API rate limit exceeded. Please wait a moment and try again.';
              break;
            case 'file_error':
              errorMessage = 'Error processing image file. Please try again.';
              break;
            case 'openai_refusal':
            case 'anthropic_refusal':
              errorMessage = 'AI service refused to process the image. Please try again with a clearer image.';
              break;
            case 'no_pet_food_detected':
              // Instead of showing an error, show the "no results" screen
              setNoProductsFound(true);
              setMatchingProducts([]);
              // Set extracted info to show the image but with "no cat food detected" reason
              setExtractedInfo({
                brand: 'No cat food detected',
                line: 'No cat food detected',
                name: 'No cat food detected',
                category: 'No cat food detected',
                texture: 'No cat food detected'
              });
              return; // Exit early to avoid setting error state
            case 'openai_api_error':
            case 'anthropic_api_error':
              errorMessage = 'AI service error occurred. Please try again.';
              break;
            default:
              errorMessage = `Error: ${data.error}`;
          }
        }

        setError(errorMessage);

        // Log additional error details for debugging
        if (data.raw_response || data.cleaned_response) {
          console.error('OCR Error Details:', {
            error_type: data.error_type,
            raw_response: data.raw_response,
            cleaned_response: data.cleaned_response
          });
        }
      }
    } catch (err) {
      console.error('Error identifying product:', err);
      setError('Error identifying product: ' + err.message);
    } finally {
      setIsProcessing(false);
      setProcessingComplete(true);
    }
  };

  const retakeImage = () => {
    setImage(null);
    setIsCaptured(false);
    setMatchingProducts([]);
    setError('');
    setNoProductsFound(false);
    setProcessingComplete(false);
    setExtractedInfo(null);
    startCamera();
  };

  const closeScanner = () => {
    setImage(null);
    setMatchingProducts([]);
    setIsCaptured(false);
    setError('');
    // Navigate back or close the scanner
    window.history.back();
  };


  return (
    <div className="scan-container">
      <div className="page-header">
        <h1 className="page-title">Product Scanner</h1>
        <img
          src="/picky-kitty/scanner.png"
          alt="The Picky Kitty Scanner"
          className="header-image"
        />
      </div>

      {!isCaptured && (
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <p style={{ marginBottom: '0.5rem', fontSize: '1.1rem', lineHeight: 1.6, textAlign: 'center' }}>
            Take a photo of any cat food product to instantly look it up in the database. Make sure the brand and product name are visible.
          </p>
        </div>
      )}

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            '& .MuiAlert-message': {
              fontSize: '1rem',
              lineHeight: 1.5
            }
          }}
          action={
            !error.includes('Daily scan limit') && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={retakeImage}
                  sx={{
                    color: 'inherit',
                    borderColor: 'currentColor',
                    '&:hover': {
                      borderColor: 'currentColor',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)'
                    }
                  }}
                >
                  Try Again
                </Button>
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => setError('')}
                  sx={{ minWidth: 'auto' }}
                >
                  <CloseIcon fontSize="small" />
                </Button>
              </Box>
            )
          }
        >
          <Typography variant="body1" component="div" sx={{ fontWeight: 500, mb: 0.5 }}>
            {error}
          </Typography>
          {error.includes('No pet food product detected') && (
            <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
              Try taking a photo of a cat food product with clear packaging and labels.
            </Typography>
          )}
          {error.includes('rate limit') && (
            <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
              Please wait a few moments before trying again.
            </Typography>
          )}
          {error.includes('authentication failed') && (
            <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
              This is a technical issue. Please contact support if the problem persists.
            </Typography>
          )}
          {error.includes('Invalid response format') && (
            <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
              The AI service had trouble processing your image. Try taking a clearer photo.
            </Typography>
          )}
          {error.includes('Daily scan limit') && (
            <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
              We've reached our daily limit for product scans. The scanner will be available again tomorrow.
            </Typography>
          )}
        </Alert>
      )}

      {!isCaptured ? (
        <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
          <div className="scan-video-container">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              className="scan-video"
            />
          </div>
          <div className="scan-button-container">
            {!stream ? (
              <Button
                variant="contained"
                onClick={() => startCamera(isBackCamera)}
                disabled={isProcessing}
              >
                {isProcessing ? <CircularProgress size={24} /> : 'Start Camera'}
              </Button>
            ) : (
              <Button variant="contained" onClick={captureImage}>
                Capture Image
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
          {!processingComplete && (
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <img
                src={image}
                alt="Captured"
                className="scan-captured-image"
              />
            </div>
          )}
                    {!processingComplete && (
            <>
              <div style={{ textAlign: 'center', marginBottom: '1rem', fontSize: '1.1rem', color: '#333', fontWeight: '500' }}>
                Does this look correct?
              </div>
              <div className="scan-button-container">
                <Tooltip title="Retake photo" arrow placement="top">
                  <button 
                    onClick={retakeImage}
                    className="scan-icon-button scan-retake-button"
                  >
                    <RefreshIcon />
                  </button>
                </Tooltip>
                <Tooltip title={isProcessing ? "Processing..." : "Confirm and analyze"} arrow placement="top">
                  <button 
                    onClick={processImage} 
                    disabled={isProcessing}
                    className="scan-icon-button scan-confirm-button"
                  >
                    {isProcessing ? <CircularProgress size={24} color="inherit" /> : <CheckIcon />}
                  </button>
                </Tooltip>
              </div>
            </>
          )}
        </div>
      )}



      {(matchingProducts.length > 0 || noProductsFound) && (
        <>
          <h2 className="scan-results-title">
            {noProductsFound ? 'No product found' : `${matchingProducts.length} possible matches found based on your photo`}
          </h2>
          {noProductsFound ? (
            <div>
              <p className="scan-no-results">
                                {extractedInfo && extractedInfo.brand === 'No cat food detected' 
                  ? (
                    <>
                      No cat food product detected.<br />
                      Try taking a clear picture of the packaging and make sure the brand and product name are visible.
                    </>
                  )
                  : 'No exact match was found for this product.'
                }
              </p>
              {(!extractedInfo || extractedInfo.brand !== 'No cat food detected') && (
                <p className="scan-no-results">
                  This might be because:<br />
                  • The brand and product name weren't clearly visible<br />
                  • <span 
                      style={{ 
                        color: '#1976d2', 
                        cursor: 'pointer', 
                        textDecoration: 'underline',
                        fontWeight: 500
                      }}
                      onClick={() => setBrandsTooltipOpen(true)}
                      onMouseEnter={(e) => e.target.style.color = '#1565c0'}
                      onMouseLeave={(e) => e.target.style.color = '#1976d2'}
                    >
                      The product isn't in the database yet
                    </span> (click to see available brands)<br />
                  • The photo was too blurry or poorly lit
                </p>
              )}
              {extractedInfo && (
                <div className="scan-extracted-info-container">
                  <div className="scan-extracted-info">
                    <h3 className="scan-extracted-info-title">
                      {extractedInfo.brand === 'No cat food detected'
                        ? 'Here’s what was found:'
                        : 'Information extracted from the photo'
                      }
                    </h3>
                    <p className="scan-extracted-info-text">
                      <strong>Brand:</strong> {extractedInfo.brand || 'Not detected'}<br />
                      <strong>Line:</strong> {extractedInfo.line || 'Not detected'}<br />
                      <strong>Name:</strong> {extractedInfo.name || 'Not detected'}<br />
                      <strong>Category:</strong> {extractedInfo.category || 'Not detected'}<br />
                      <strong>Texture:</strong> {extractedInfo.texture || 'Not detected'}
                    </p>
                  </div>
                  <div className="scan-extracted-image">
                    <img
                      src={image}
                      alt="Captured product"
                      className="scan-extracted-image-thumbnail"
                    />
                  </div>
                </div>
              )}
              <PickyKittyBubble mt={3}>
                If this product isn't in my database yet, I'll work on adding it shortly!
              </PickyKittyBubble>
            </div>
          ) : (
            <div className="data-grid-container">
              <Box sx={{ width: '100%' }}>
                <CardView cards={matchingProducts} />
              </Box>
            </div>
          )}
          
          {/* Amazon Associate Disclaimer - Only show when results are found */}
          {matchingProducts.length > 0 && (
            <Box sx={{ 
              mt: 3, 
              mb: 0.25, 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center'
            }}>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'text.secondary',
                  fontStyle: 'italic',
                  textAlign: 'center',
                  fontSize: '0.85rem'
                }}
              >
                As an Amazon Associate, The Picky Kitty earns from qualifying purchases.{" "}
                <a 
                  href="/amazon-disclosure" 
                  style={{ 
                    color: 'inherit', 
                    textDecoration: 'underline',
                    fontWeight: '500'
                  }}
                >
                  Learn more
                </a>
              </Typography>
            </Box>
          )}
          
          <div className="scan-start-again-button">
            <Button
              variant="contained"
              onClick={retakeImage}
            >
              Try another picture
            </Button>
          </div>
        </>
      )}
      
      {/* Picky Kitty Speech Bubble - shown below camera when not yet captured */}
      {!isCaptured && (
        <PickyKittyBubble mt={4}>
          I might not know every product yet, but if I miss one, I'll track it down!
        </PickyKittyBubble>
      )}

      <BrandsTooltip 
        open={brandsTooltipOpen} 
        onClose={() => setBrandsTooltipOpen(false)} 
      />
    </div>
  );
};

export default Scanner; 